// ─── 全局标志（挂到 window 避免多版本作用域问题）────────────────────────────
window.__fitmentPendingMaker        = window.__fitmentPendingMaker        ?? null;
window.__fitmentPendingModel        = window.__fitmentPendingModel        ?? null;
window.__fitmentSubmitting          = window.__fitmentSubmitting          ?? false;
window.__fitmentFilterUpdateRegistered = window.__fitmentFilterUpdateRegistered ?? false;
window.__fitmentEventsRegistered       = window.__fitmentEventsRegistered       ?? false;

// ─── 参数名约定 ───────────────────────────────────────────────────────────────
// 车型筛选参数：filter.p.m.custom.fit_motor_ids
// 值 = Laravel models.id（API 的 id）。旧的 filter.p.m.custom.fitment 已在模板层屏蔽渲染。
const FITMENT_PARAM_KEYWORD = 'fit_motor_ids';

// 默认走 Shopify App Proxy；实际地址由 snippets/fitment-filter.liquid 注入，
// 方便本地调试时临时切到本机 Laravel。
const DEFAULT_DATA_URL    = '/apps/proxy-api/motor-list';
const DEFAULT_MAPPING_URL = '/apps/proxy-api/model-mapping';

// ─── 基础工具 ─────────────────────────────────────────────────────────────────

// API 的 id 是数字，URL 参数是字符串：所有比较一律用字符串，否则新旧数据混用时会整体失配。
function toId(value) {
    return String(value ?? '').trim();
}

function escapeAttr(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

// 同一个 section 里可能有多个容器（桌面筛选面板 + 移动端抽屉），要全部渲染。
function getFitmentLists(column) {
    return Array.from(document.querySelectorAll(`[data-fitment-list="${column}"]`));
}

// ─── API ──────────────────────────────────────────────────────────────────────

let vehiclesCache   = null;
let fitmentInstance = null;

async function fetchAllVehicles(dataUrl) {
    if (vehiclesCache) return vehiclesCache;
    const res = await fetch(dataUrl);
    if (!res.ok) throw new Error(`[FitmentFilter] fetch failed: ${res.status}`);
    vehiclesCache = await res.json();
    return vehiclesCache;
}

// ─── 数据解析 ──────────────────────────────────────────────────────────────────

let modelMergeMap = {};

function normalize(str) {
    return (str || '').replace(/\s+/g, '').trim();
}

// 车型显示名：**不对 API 返回的原文做任何改动**（空格、大小写、括号全部保留）。
// 只有查 model 合并表时才用去掉空格的 key；命中合并规则时用映射表里的目标名。
function resolveModelLabel(rawModel, maker) {
    const key = normalize(rawModel);
    const lookup = modelMergeMap[key];
    // 支持 { "CB750HORNET": "CB750" } 或 { "CB750HORNET": { "maker": "HONDA", "target": "CB750" } }
    if (typeof lookup === 'string') return lookup;
    if (lookup?.target && (!lookup.maker || normalize(lookup.maker) === normalize(maker))) return lookup.target;
    return rawModel ?? '';
}

// Model 映射缓存配置
const MODEL_MAPPING_CACHE_KEY = 'fitment_model_mapping';
const MODEL_MAPPING_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24小时

/**
 * 检查缓存是否有效
 */
function isModelMappingCacheValid() {
    try {
        const cached = localStorage.getItem(MODEL_MAPPING_CACHE_KEY);
        if (!cached) return false;
        
        const { timestamp } = JSON.parse(cached);
        return (Date.now() - timestamp) < MODEL_MAPPING_CACHE_DURATION;
    } catch (e) {
        return false;
    }
}

/**
 * 从 localStorage 读取缓存
 */
function getModelMappingFromCache() {
    try {
        const cached = localStorage.getItem(MODEL_MAPPING_CACHE_KEY);
        if (cached) {
            const { data } = JSON.parse(cached);
            return data;
        }
    } catch (e) {
        // 缓存损坏，清除
        localStorage.removeItem(MODEL_MAPPING_CACHE_KEY);
    }
    return null;
}

/**
 * 保存映射到 localStorage
 */
function saveModelMappingToCache(data) {
    try {
        localStorage.setItem(MODEL_MAPPING_CACHE_KEY, JSON.stringify({
            timestamp: Date.now(),
            data: data
        }));
    } catch (e) {
        // localStorage 可能已满或被禁用，忽略
    }
}

/**
 * 加载 model 合并映射表（优先使用缓存）
 */
async function loadModelMergeMap(mappingUrl) {
    const url = mappingUrl || DEFAULT_MAPPING_URL;
    // 切到本地/自定义地址调试时不走 localStorage 缓存，避免被线上缓存数据掩盖
    const useCache = url === DEFAULT_MAPPING_URL;

    // 1. 检查缓存是否有效
    if (useCache && isModelMappingCacheValid()) {
        const cached = getModelMappingFromCache();
        if (cached) {
            modelMergeMap = cached;
            return;
        }
    }
    
    // 2. 缓存无效，请求新数据
    try {
        const res = await fetch(url);
        if (res.ok) {
            modelMergeMap = await res.json();
            // 保存到缓存
            if (useCache) saveModelMappingToCache(modelMergeMap);
        }
    } catch (e) {
        // 静默失败，映射表为空即可
    }
}

function filterVehiclesByVendor(vehicles, vendor) {
    if (!vendor) return vehicles;
    const v = vendor.toUpperCase();
    return vehicles.filter(item =>
        Array.isArray(item.brands) &&
        item.brands.some(b => (b || '').toUpperCase() === v)
    );
}

function getCurrentVendor() {
    const params = new URLSearchParams(window.location.search);
    return params.get('filter.p.vendor') || null;
}

function buildTree(vehicles) {
    const tree = new Map();
    vehicles.forEach(({ id, maker, model }) => {
        const mid = toId(id);
        const m = maker ?? '';
        const d = resolveModelLabel(model, m);
        // 空值判断用归一化结果，但入树的键/显示名都是 API 原文
        if (!normalize(m) || !normalize(d) || !mid) return;
        if (!tree.has(m)) tree.set(m, new Map());
        const modelMap = tree.get(m);
        if (!modelMap.has(d)) modelMap.set(d, []);
        modelMap.get(d).push(mid);
    });
    return tree;
}

function buildVehicleMap(vehicles) {
    const map = new Map();
    vehicles.forEach(({ id, maker, model }) => {
        map.set(toId(id), { maker: maker ?? '', model: resolveModelLabel(model, maker) });
    });
    return map;
}

function getActiveGidsFromUrl(paramName) {
    if (!paramName) return [];
    const params = new URLSearchParams(window.location.search);
    // list metafield 既可能是重复参数，也可能是逗号分隔，两种都兼容。
    return params.getAll(paramName)
        .flatMap(value => String(value).split(','))
        .map(toId)
        .filter(Boolean);
}

// ─── 状态查找 ──────────────────────────────────────────────────────────────────

function findActiveMaker(tree, activeGids) {
    if (!activeGids?.length) return null;
    const active = new Set(activeGids.map(toId));
    for (const [maker, modelMap] of tree) {
        for (const [, ids] of modelMap) {
            if (ids.some(id => active.has(toId(id)))) return maker;
        }
    }
    return null;
}

function findActiveModel(tree, maker, activeGids) {
    if (!activeGids?.length || !maker || !tree.has(maker)) return null;
    const active = new Set(activeGids.map(toId));
    for (const [model, ids] of tree.get(maker)) {
        if (ids.some(id => active.has(toId(id)))) return model;
    }
    return null;
}

// ─── DOM 渲染 ──────────────────────────────────────────────────────────────────

function renderList(column, items, activeValue, paramName) {
    const lists = getFitmentLists(column);
    if (!lists.length) return;

    const isModel = column === 'model';

    lists.forEach((ul, listIndex) => {
        ul.innerHTML = '';

        items.forEach(({ label, ids }, index) => {
            // id 必须在整页唯一：桌面面板和抽屉会各渲染一份，否则 label 会点到另一份上
            const id = `fitment-${column}-${listIndex}-${index}`;
            const checked = label === activeValue;
            const [primaryId, ...extraIds] = ids;

            const nameAttr = isModel && paramName
                ? ` name="${escapeAttr(paramName)}" value="${escapeAttr(primaryId ?? '')}"`
                : '';

            // 极少数车型对应多个 ID（如 DUCATI 1098S）：额外 ID 用 hidden input 承载，
            // 未选中时 disabled，这样原生筛选表单序列化时不会带上未选中的值。
            const extraInputs = isModel && paramName
                ? extraIds.map(extraId =>
                    `<input type="hidden" class="fitment-extra-id" name="${escapeAttr(paramName)}" value="${escapeAttr(extraId)}"${checked ? '' : ' disabled'}>`
                  ).join('')
                : '';

            const li = document.createElement('li');
            li.className = 'facets__inputs-list-item fitment-item';
            li.innerHTML = `
                <label class="checkbox" for="${id}">
                    <input
                        type="checkbox"
                        id="${id}"
                        class="fitment-checkbox"${nameAttr}
                        data-column="${escapeAttr(column)}"
                        data-value="${escapeAttr(label)}"
                        data-ids="${escapeAttr(ids.join(','))}"
                        ${checked ? 'checked' : ''}
                    >
                    <span class="checkbox__label">
                        <span class="checkbox__label-text">${escapeAttr(label)}</span>
                    </span>
                </label>
                ${extraInputs}
            `;
            ul.appendChild(li);
        });
    });
}

const PRIORITY_MAKERS = ['HONDA', 'KAWASAKI', 'SUZUKI', 'YAMAHA'];

function renderMakers(tree, activeMaker, paramName) {
    const items = [];
    tree.forEach((_, maker) => items.push({ label: maker, ids: [] }));
    items.sort((a, b) => {
        const ai = PRIORITY_MAKERS.indexOf(a.label.toUpperCase());
        const bi = PRIORITY_MAKERS.indexOf(b.label.toUpperCase());
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.label.localeCompare(b.label);
    });
    renderList('maker', items, activeMaker, paramName);
}

function renderModels(tree, maker, activeModel, paramName) {
    if (!maker || !tree.has(maker)) {
        getFitmentLists('model').forEach(ul => { ul.innerHTML = ''; });
        return;
    }
    const items = [];
    tree.get(maker).forEach((ids, model) => items.push({ label: model, ids }));
    items.sort((a, b) => a.label.localeCompare(b.label));
    renderList('model', items, activeModel, paramName);
}

function toggleSection(column, visible) {
    document.querySelectorAll(`.fitment-filter[data-fitment-column="${column}"]`).forEach((section) => {
        section.style.opacity       = visible ? '1' : '0.3';
        section.style.pointerEvents = visible ? '' : 'none';
    });
}

// ─── FitmentFilter ────────────────────────────────────────────────────────────

class FitmentFilter {
    constructor(config) {
        this.config        = config;
        this.tree          = null;
        this.vehicleMap    = null;
        this.selectedMaker = null;
        this.selectedModel = null;
        this.activeGids    = [];
        this.lastMakerCount = 0;
    }

    async init() {
        try {
            const vehicles     = await fetchAllVehicles(this.config.dataUrl);
            const vendor       = getCurrentVendor();
            const filtered     = filterVehiclesByVendor(vehicles, vendor);
            this.allVehicles   = vehicles;
            this.currentVendor = vendor;
            this.tree          = buildTree(filtered);
            this.vehicleMap    = buildVehicleMap(filtered);
            this.activeGids    = getActiveGidsFromUrl(this.config.paramName);
            this.selectedMaker = findActiveMaker(this.tree, this.activeGids);
            this.selectedModel = findActiveModel(this.tree, this.selectedMaker, this.activeGids);
            this.render();
            this.attachEvents();
        } catch (err) {
            console.error('[FitmentFilter] init error:', err);
        }
    }

    rebuildTreeForVendor(vendor) {
        if (vendor === this.currentVendor) return;
        const filtered = filterVehiclesByVendor(this.allVehicles, vendor);
        this.currentVendor = vendor;
        this.tree          = buildTree(filtered);
        this.vehicleMap    = buildVehicleMap(filtered);

        // 如果当前选中的 maker/model 不在新 tree 中，清除选择
        if (this.selectedMaker && !this.tree.has(this.selectedMaker)) {
            this.selectedMaker = null;
            this.selectedModel = null;
            this.activeGids    = [];
        } else if (this.selectedModel && this.selectedMaker) {
            const modelMap = this.tree.get(this.selectedMaker);
            if (!modelMap || !modelMap.has(this.selectedModel)) {
                this.selectedModel = null;
                this.activeGids    = [];
            }
        }
    }

    render() {
        const paramName = this.config.paramName;
        this.lastMakerCount = this.tree ? this.tree.size : 0;
        renderMakers(this.tree, this.selectedMaker, paramName);
        renderModels(this.tree, this.selectedMaker, this.selectedModel, paramName);
        toggleSection('model', this.selectedMaker !== null);
        this.renderActiveBadge();
    }

    renderActiveBadge() {
        const containers = document.querySelectorAll('.facets-remove');
        if (!containers.length) return;

        containers.forEach(container =>
            container.querySelectorAll('.fitment-active-badge').forEach(el => el.remove())
        );

        // 以 URL 为准：URL 里没有筛选值就不显示 badge
        const active = getActiveGidsFromUrl(this.config.paramName);
        if (!active.length) return;

        let label = '';
        if (this.selectedMaker && this.selectedModel) {
            label = `${this.selectedMaker} ${this.selectedModel}`;
        } else {
            // 兜底：URL 带值但推导不出 maker/model（API 缺该 ID、被 vendor 过滤掉等）
            const found = this.vehicleMap?.get(active[0]);
            label = found ? `${found.maker} ${found.model}` : active.join(', ');
        }

        const params   = new URLSearchParams(window.location.search);
        params.delete(this.config.paramName);
        const qs       = params.toString();
        const clearUrl = `${window.location.pathname}${qs ? '?' + qs : ''}`;

        const template = document.querySelector(
            '.facets-remove__pill:not(.fitment-active-badge)'
        );
        const svgContent = template?.querySelector('.svg-wrapper')?.innerHTML ?? '×';

        containers.forEach((container) => {
            const badge = document.createElement('facet-remove-component');
            badge.className   = 'pills__pill pills__pill--desktop-small facets-remove__pill fitment-active-badge';
            badge.dataset.url = clearUrl;
            badge.setAttribute('tabindex', '0');
            badge.setAttribute('role', 'button');
            badge.setAttribute('on:click', '/removeFilter?form=');
            badge.setAttribute('on:keydown', '/removeFilter?form=');
            badge.innerHTML = `
                ${escapeAttr(label)}
                <span class="svg-wrapper svg-wrapper--smaller">${svgContent}</span>
                <span class="visually-hidden">Remove</span>
            `;
            container.prepend(badge);
        });
    }

    // 只注册一次全局 change 监听：section 重渲染会创建新实例，
    // 若每个实例都注册，一次点击会被重复提交多次。
    attachEvents() {
        if (window.__fitmentEventsRegistered) return;
        window.__fitmentEventsRegistered = true;

        document.addEventListener('change', (e) => {
            const instance = fitmentInstance;
            if (!instance) return;

            const cb = e.target;
            if (!cb.classList.contains('fitment-checkbox')) return;

            const column = cb.dataset.column;
            const value  = cb.dataset.value;

            // 同一份列表在桌面面板和抽屉里各有一份，勾选状态需要同步
            document.querySelectorAll(
                `.fitment-checkbox[data-column="${column}"]`
            ).forEach(el => {
                if (el === cb) return;
                el.checked = false;
                // 多 ID 车型的隐藏补充项一并关掉，避免残留值被原生表单序列化带上
                el.closest('li')?.querySelectorAll('.fitment-extra-id')
                    .forEach(input => { input.disabled = true; });
            });

            if (column === 'maker') {
                instance.selectedMaker           = cb.checked ? value : null;
                instance.selectedModel           = null;
                instance.activeGids              = [];
                window.__fitmentPendingMaker     = null;
                window.__fitmentPendingModel     = null;
                renderModels(instance.tree, instance.selectedMaker, null, instance.config.paramName);
                toggleSection('model', instance.selectedMaker !== null);
                instance.renderActiveBadge();
                // maker 只做下钻，不参与筛选；取消勾选时清掉 URL 上的车型参数
                if (!cb.checked) instance.submitFilter([]);

            } else if (column === 'model') {
                // 该车型对应多个 ID 时，隐藏补充项随勾选状态启用/禁用
                cb.closest('li')?.querySelectorAll('.fitment-extra-id')
                    .forEach(input => { input.disabled = !cb.checked; });

                if (cb.checked) {
                    instance.selectedModel           = value;
                    instance.activeGids              = cb.dataset.ids.split(',').filter(Boolean);
                    window.__fitmentPendingMaker     = instance.selectedMaker;
                    window.__fitmentPendingModel     = instance.selectedModel;
                    instance.submitFilter(instance.activeGids);
                } else {
                    instance.selectedModel           = null;
                    instance.activeGids              = [];
                    window.__fitmentPendingMaker     = null;
                    window.__fitmentPendingModel     = null;
                    instance.renderActiveBadge();
                    instance.submitFilter([]);
                }
            }
        });
    }

    submitFilter(ids) {
        const paramName = this.config.paramName;
        if (!paramName) return;

        const currentParams = new URLSearchParams(window.location.search);
        const newParams     = new URLSearchParams();

        currentParams.forEach((val, key) => {
            // 丢掉本筛选器自己的参数（否则会与下面 append 的值叠加成多值 OR）与分页
            if (key === paramName || key === 'page') return;
            newParams.append(key, val);
        });

        ids.forEach(id => newParams.append(paramName, toId(id)));

        const url = `${window.location.pathname}?${newParams.toString()}`;
        this.updateFacets(url);
    }

    updateFacets(url) {
        const facetsForm = document.querySelector('facets-form-component');
        if (facetsForm?.updateFiltersByURL) {
            window.__fitmentSubmitting = true;
            try {
                facetsForm.updateFiltersByURL(url);
            } finally {
                queueMicrotask(() => { window.__fitmentSubmitting = false; });
            }
        } else {
            window.location.href = url;
        }
    }
}

// ─── 拦截原生 filter 提交 ─────────────────────────────────────────────────────

function interceptFacetForm(paramName) {
    if (!paramName) return;

    let clickedVendor = null;

    document.addEventListener('click', (e) => {
        const input = e.target.closest('input[type="checkbox"]');
        if (!input) return;
        if (input.name === 'filter.p.vendor') {
            clickedVendor = input.checked ? input.value : null;
        }
    }, true);

    const originalPushState = history.pushState.bind(history);
    history.pushState = function(state, title, url) {
        if (!window.__fitmentSubmitting && url && typeof url === 'string' && clickedVendor) {
            const parsed = new URL(url, location.origin);
            if (parsed.searchParams.has('filter.p.vendor')) {
                const kept = [];
                parsed.searchParams.forEach((value, key) => {
                    // 换 vendor 时清掉车型下钻（可能已不适用于新 vendor），其余筛选（价格、库存…）保留
                    if (key === paramName) return;
                    if (key === 'filter.p.vendor') return;
                    kept.push([key, value]);
                });

                parsed.search = '';
                kept.forEach(([key, value]) => parsed.searchParams.append(key, value));
                parsed.searchParams.set('filter.p.vendor', clickedVendor);

                url                          = parsed.pathname + (parsed.search ? parsed.search : '');
                clickedVendor                = null;
                window.__fitmentPendingMaker = null;
                window.__fitmentPendingModel = null;
            }
        }
        return originalPushState(state, title, url);
    };
}

// ─── filter:update 处理 ───────────────────────────────────────────────────────

// ─── sectionRenderer 异步 DOM 替换监控 ─────────────────────────────────────────

let __sectionRerenderObserver = null;
let __sectionRerenderDebounce = null;
/**
 * 启动永久监控：当 sectionRenderer 异步替换 section DOM 导致 <ul> li 被清空时，
 * 防抖后重新渲染 fitment 列表。
 */
function startSectionRerenderWatch() {
    if (__sectionRerenderObserver) return; // 已启动，不重复

    // 监控 document.body，因为 sectionRenderer 可能替换整个 section
    __sectionRerenderObserver = new MutationObserver(() => {
        clearTimeout(__sectionRerenderDebounce);
        __sectionRerenderDebounce = setTimeout(() => {
            const instance = fitmentInstance;

            // 没有数据时不要空转（否则空列表会触发无限重渲染）
            if (!instance?.tree || !instance.lastMakerCount) return;

            const makerEmpty = getFitmentLists('maker').some(ul => ul.children.length === 0);
            const modelEmpty = instance.selectedMaker !== null
                && getFitmentLists('model').some(ul => ul.children.length === 0);

            if (makerEmpty || modelEmpty) {
                instance.render();
            }
        }, 150);
    });

    __sectionRerenderObserver.observe(document.body, { childList: true, subtree: true });
}

function onFilterUpdate() {
    if (!fitmentInstance?.tree) {
        fitmentInstance = initFitmentFilter();
        return;
    }

    // 检测 vendor 变化，重建 tree
    const vendor = getCurrentVendor();
    fitmentInstance.rebuildTreeForVendor(vendor);

    if (window.__fitmentPendingMaker) {
        fitmentInstance.selectedMaker        = window.__fitmentPendingMaker;
        fitmentInstance.selectedModel        = window.__fitmentPendingModel;
        fitmentInstance.activeGids           = getActiveGidsFromUrl(fitmentInstance.config.paramName);
        window.__fitmentPendingMaker         = null;
        window.__fitmentPendingModel         = null;
    } else {
        fitmentInstance.activeGids    = getActiveGidsFromUrl(fitmentInstance.config.paramName);
        fitmentInstance.selectedMaker = findActiveMaker(fitmentInstance.tree, fitmentInstance.activeGids);
        fitmentInstance.selectedModel = findActiveModel(fitmentInstance.tree, fitmentInstance.selectedMaker, fitmentInstance.activeGids);
    }

    fitmentInstance.render();
}

// ─── 启动 ─────────────────────────────────────────────────────────────────────

function initFitmentFilter() {
    const config = window.__fitmentConfig;
    if (!config) {
        console.warn('[FitmentFilter] 缺少 __fitmentConfig');
        return null;
    }

    // 地址由 snippets/fitment-filter.liquid 注入；缺省回退到 App Proxy
    config.dataUrl    = config.dataUrl    || DEFAULT_DATA_URL;
    config.mappingUrl = config.mappingUrl || DEFAULT_MAPPING_URL;

    if (!config.paramName || !config.paramName.includes(FITMENT_PARAM_KEYWORD)) {
        console.warn('[FitmentFilter] 参数名不是车型筛选参数:', config.paramName);
    }

    // https 页面请求 http:// 会被浏览器按混合内容拦掉，提前给出明确提示
    if (window.location.protocol === 'https:' && /^http:\/\//i.test(config.dataUrl)) {
        console.warn(
            '[FitmentFilter] 混合内容：https 页面无法请求',
            config.dataUrl,
            '——请用 http 本地预览（shopify theme dev），或把 fitment_api_base 改回 /apps/proxy-api'
        );
    }

    const instance = new FitmentFilter(config);
    // 先加载合并映射表，再初始化 instance
    loadModelMergeMap(config.mappingUrl).then(() => instance.init()).then(() => {
        interceptFacetForm(config.paramName);
        startSectionRerenderWatch();
    });
    return instance;
}

document.addEventListener('DOMContentLoaded', () => {
    fitmentInstance = initFitmentFilter();
});

document.addEventListener('shopify:section:load', () => {
    vehiclesCache   = null;
    fitmentInstance = initFitmentFilter();
});

// 防止重复注册 filter:update
if (!window.__fitmentFilterUpdateRegistered) {
    window.__fitmentFilterUpdateRegistered = true;
    document.addEventListener('filter:update', onFilterUpdate);
}